import { useCallback } from "react";
import dayjs from "dayjs";
import { Button, Separator } from "@radix-ui/themes";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import DropdownMenu from "../dropdown-menu/DropdownMenu";
import { DatePickerWithRange } from "./picker";
import styles from "./DateRangePicker.module.scss";

export type DateRange = { dateFrom: string; dateTo: string };

export const dateRangePresets = [
  "today",
  "1d",
  "7d",
  "14d",
  "1m",
  "2m",
  "3m",
  "6m",
] as const;
export type DateRangePreset = (typeof dateRangePresets)[number];

export function isDateRangePreset(obj: any): obj is DateRangePreset {
  return dateRangePresets.includes(obj);
}

const dateRangePresetToLabel: Record<DateRangePreset, string> = {
  today: "Today",
  "1d": "Yesterday",
  "7d": "Last 7 Days",
  "14d": "Last 14 Days",
  "1m": "Last 1 Month",
  "2m": "Last 2 Months",
  "3m": "Last 3 Months",
  "6m": "Last 6 Months",
};

export const buildRange: Record<DateRangePreset, () => DateRange> = {
  today: () => ({
    dateFrom: dayjs().startOf("day").toISOString(),
    dateTo: dayjs().toISOString(),
  }),
  "1d": () => ({
    dateFrom: dayjs().startOf("day").subtract(1, "day").toISOString(),
    dateTo: dayjs().endOf("day").subtract(1, "day").toISOString(),
  }),
  "7d": () => ({
    dateFrom: dayjs().startOf("day").subtract(7, "day").toISOString(),
    dateTo: dayjs().toISOString(),
  }),
  "14d": () => ({
    dateFrom: dayjs().startOf("day").subtract(14, "day").toISOString(),
    dateTo: dayjs().toISOString(),
  }),
  "1m": () => ({
    dateFrom: dayjs().startOf("day").subtract(1, "month").toISOString(),
    dateTo: dayjs().toISOString(),
  }),
  "2m": () => ({
    dateFrom: dayjs().startOf("day").subtract(2, "month").toISOString(),
    dateTo: dayjs().toISOString(),
  }),
  "3m": () => ({
    dateFrom: dayjs().startOf("day").subtract(3, "month").toISOString(),
    dateTo: dayjs().toISOString(),
  }),
  "6m": () => ({
    dateFrom: dayjs().startOf("day").subtract(6, "month").toISOString(),
    dateTo: dayjs().toISOString(),
  }),
};

interface DateRangePickerProps {
  value: DateRange;
  onChange: (newDateRange: DateRange) => void;
  hideRange?: boolean;
  size?: "1" | "2" | "3";
}

export function DateRangePicker({
  value,
  onChange,
  hideRange,
  size = "1",
}: DateRangePickerProps) {
  const { dateFrom, dateTo } = value;

  const getDateRangeLabel = useCallback(
    (dateFrom: string, dateTo: string): string => {
      const preset = dateRangePresets.find((preset) => {
        const range = buildRange[preset]();

        return (
          dayjs(range.dateFrom).format("YYYY-MM-DD") ===
            dayjs(dateFrom).format("YYYY-MM-DD") &&
          dayjs(range.dateTo).format("YYYY-MM-DD") ===
            dayjs(dateTo).format("YYYY-MM-DD")
        );
      });

      return preset ? dateRangePresetToLabel[preset] : "Custom";
    },
    []
  );

  return (
    <>
      <div className={styles.Trigger}>
        <DropdownMenu
          items={dateRangePresets.map((item) => ({
            label: dateRangePresetToLabel[item],
            onClick: () => onChange(buildRange[item]()),
          }))}
        >
          <DropdownMenu.Trigger>
            <Button
              color={"gray"}
              variant="soft"
              size={size}
              highContrast
              className="border-0 rounded-[3px]"
            >
              {getDateRangeLabel(dateFrom, dateTo)}

              <ChevronDownIcon />
            </Button>
          </DropdownMenu.Trigger>
        </DropdownMenu>

        {!hideRange && (
          <>
            <Separator orientation="vertical" />

            <DatePickerWithRange
              className="border-0 rounded-[3px]"
              size={size}
              {...{ dateFrom, dateTo, setDateRange: onChange }}
            />
          </>
        )}
      </div>
    </>
  );
}
